/**
 * Frontend permission helpers.
 *
 * These mirror server-side gates so the UI doesn't fire forbidden requests
 * that the backend would 403. Each helper has a comment pointing to the
 * authoritative backend rule — if the server check moves, update here too
 * (and the E2E tripwire will catch divergence on the next CI run).
 *
 * Why a module instead of inline `user.role === "owner"` checks: those
 * arrays drift. We had 9+ files with hardcoded role lists, and the layout
 * was firing 403s for owner on every page load because Sidebar's role
 * gate diverged from FleetPage's. Centralizing here makes "frontend
 * permission = backend permission" a single source of truth.
 */

export type Role =
  | "ops_manager"
  | "pilot"
  | "owner"
  | "mechanic"
  | "super_admin"
  | "accountant"
  | "ground_support";

export type UserLike =
  | { role?: Role | string | null; etm_enabled?: boolean | null }
  | null
  | undefined;

function roleOf(user: UserLike): string | null {
  return user?.role ?? null;
}

// ── ETM ────────────────────────────────────────────────────────────────────
// Backend: /api/etm/* gated to ops_manager/super_admin/pilot/mechanic AND the
// global etm_enabled kill switch (settings.etm_enabled, surfaced on /auth/me).
// When ETM is paused for prod the backend endpoints return empty payloads; we
// also hide the nav item, fleet panel and config tabs so nothing ETM shows.
// Treat the flag as enabled unless it is explicitly false (back-compat with
// cached user payloads that predate the flag).
export function canReadETM(user: UserLike): boolean {
  if (user?.etm_enabled === false) return false;
  const r = roleOf(user);
  // super_admin is intentionally excluded: it operates cross-tenant with no
  // company_id, so the company-scoped /etm/* endpoints 403 for it. Leaving it in
  // fired a noisy 403 against /etm/alerts on every super_admin page load
  // (audit 2026-06-04). super_admin inspects ETM via the /superadmin/* proxy.
  return r === "ops_manager" || r === "pilot" || r === "mechanic";
}

// ── Staff roster ───────────────────────────────────────────────────────────
// Backend: backend/routes/core.py:319 explicitly 403s owner on GET /staff.
// All other authenticated roles are allowed (returns same-company users).
export function canReadStaff(user: UserLike): boolean {
  return roleOf(user) !== "owner";
}

// ── Expenses / fuel-analytics ──────────────────────────────────────────────
// Backend filters by company + (for owner) requires explicit per-flag access.
// Frontend gate here keeps us from prefetching a list that'll come back
// empty or 403 depending on the flag config.
export function canReadExpenses(user: UserLike): boolean {
  const r = roleOf(user);
  if (r === "owner") {
    // Owner expenses access is per-flag (can_see_expenses on the owner record).
    // We can't read that flag here cheaply; default to TRUE and let the
    // backend filter the response. The 403 path here is rare; 200-empty is
    // the common case.
    return true;
  }
  // pilot / mechanic / ground_support don't have an expenses surface today.
  return r === "ops_manager" || r === "super_admin" || r === "accountant";
}

// ── Aircraft list ──────────────────────────────────────────────────────────
// Backend returns same-company aircraft for any authenticated user. No 4xx
// path. Helper exists so a future restriction (e.g., owner-only-own) lands
// in one place.
export function canReadAircraft(_user: UserLike): boolean {
  return true;
}

// ── Maintenance / hours-log ────────────────────────────────────────────────
// Backend: pilots see assigned aircraft only; mechanic + ops_manager see
// all company aircraft; owner sees their own aircraft; accountant + ground
// support are NOT in maintenance flows.
export function canReadMaintenance(user: UserLike): boolean {
  const r = roleOf(user);
  return (
    r === "ops_manager" || r === "super_admin" || r === "pilot" || r === "mechanic" || r === "owner"
  );
}

// ── Companies (cross-tenant admin) ─────────────────────────────────────────
// Backend: super_admin only.
export function canManageCompanies(user: UserLike): boolean {
  return roleOf(user) === "super_admin";
}
